/**
 * The `palette` module: a community's colour palettes (groups → themes →
 * variants) and the shared dictionary of vendors, colours and colour types.
 * Server half: app/resources/plugins/arc/palette/.
 */

const seg = encodeURIComponent;

export default {
  // The module's pages load on the first route that needs them (core/js/modules.js);
  // the shell warms them in idle time after sign-in. Boot carries the shell only.
  load: () => Promise.all([import('../components/palettes.js')]),
  name: 'palette',

  nav: {
    area: 'Palettes',
    items: [
      { route: 's/:site/palettes',  label: 'Community palettes', sitePerm: 'ARBView' },
      { route: 'palette/colors',    label: 'Colours',      perm: 'Admin' },
      { route: 'palette/vendors',   label: 'Vendors',      perm: 'Admin' },
      { route: 'palette/types',     label: 'Colour types', perm: 'Admin' },
    ],
  },

  routes: {
    '/s/:site/palettes':  'arc-palettes',
    '/s/:site/palettes/:groupId/:themeId': 'arc-palette-theme',
    '/palette/colors':    'arc-palette-colors',
    '/palette/vendors':   'arc-palette-vendors',
    '/palette/types':     'arc-palette-types',
  },

  resources: (api) => ({
    palette: {
      // A community's groups, themes, variants and their colours.
      groups: (siteId, params) => api.get(`/sites/${seg(siteId)}/palette/groups`, params),
      group: (siteId, id) => api.get(`/sites/${seg(siteId)}/palette/groups/${seg(id)}`),
      createGroup: (siteId, body) => api.post(`/sites/${seg(siteId)}/palette/groups`, body),
      updateGroup: (siteId, id, body) => api.put(`/sites/${seg(siteId)}/palette/groups/${seg(id)}`, body),
      deleteGroup: (siteId, id) => api.del(`/sites/${seg(siteId)}/palette/groups/${seg(id)}`),
      themes: (siteId, groupId) => api.get(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes`),
      theme: (siteId, groupId, id, params) => api.get(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(id)}`, params),
      createTheme: (siteId, groupId, body) => api.post(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes`, body),
      updateTheme: (siteId, groupId, id, body) => api.put(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(id)}`, body),
      deleteTheme: (siteId, groupId, id) => api.del(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(id)}`),
      variants: (siteId, groupId, themeId) => api.get(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(themeId)}/variants`),
      variant: (siteId, groupId, themeId, id) => api.get(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(themeId)}/variants/${seg(id)}`),
      createVariant: (siteId, groupId, themeId, body) => api.post(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(themeId)}/variants`, body),
      updateVariant: (siteId, groupId, themeId, id, body) => api.put(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(themeId)}/variants/${seg(id)}`, body),
      deleteVariant: (siteId, groupId, themeId, id) => api.del(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(themeId)}/variants/${seg(id)}`),
      cloneVariant: (siteId, groupId, themeId, id) => api.post(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(themeId)}/variants/${seg(id)}/clone`, {}),
      variantColors: (siteId, groupId, themeId, id) => api.get(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(themeId)}/variants/${seg(id)}/colors`),
      setVariantColor: (siteId, groupId, themeId, id, body) => api.post(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(themeId)}/variants/${seg(id)}/colors`, body),
      removeVariantColor: (siteId, groupId, themeId, id, rowId) => api.del(`/sites/${seg(siteId)}/palette/groups/${seg(groupId)}/themes/${seg(themeId)}/variants/${seg(id)}/colors/${seg(rowId)}`),
      // Every colour offered in a community (its vendors' and the shared ones).
      siteColors: (siteId, params) => api.get(`/sites/${seg(siteId)}/palette/colors`, params),
      // The shared dictionary.
      colors: (params) => api.get('/palette/colors', params),
      color: (id) => api.get(`/palette/colors/${seg(id)}`),
      createColor: (body) => api.post('/palette/colors', body),
      updateColor: (id, body) => api.put(`/palette/colors/${seg(id)}`, body),
      deleteColor: (id) => api.del(`/palette/colors/${seg(id)}`),
      uploadColorImage: (id, file) => api.upload(`/palette/colors/${seg(id)}/image`, file),
      removeColorImage: (id) => api.del(`/palette/colors/${seg(id)}/image`),
      vendors: () => api.get('/palette/vendors'),
      createVendor: (body) => api.post('/palette/vendors', body),
      updateVendor: (id, body) => api.put(`/palette/vendors/${seg(id)}`, body),
      deleteVendor: (id) => api.del(`/palette/vendors/${seg(id)}`),
      types: () => api.get('/palette/colortypes'),
      createType: (body) => api.post('/palette/colortypes', body),
      updateType: (id, body) => api.put(`/palette/colortypes/${seg(id)}`, body),
      deleteType: (id) => api.del(`/palette/colortypes/${seg(id)}`),
    },
  }),
};
